import { fromStream } from "mobx-utils";
import { assign, createActor, enqueueActions, fromPromise, setup } from "xstate";
import { Section } from "./types";
import { Step } from "../steps/v2/step";
import { debugLog } from "@/lib/log";

export type AvatarMediaEvent =
  | { type: "mediaCanPlay" }
  | { type: "mediaPlaying" }
  | { type: "mediaPaused" }
  | { type: "mediaFinished" };

export type AvatarEvent =
  | { type: "timeUpdate"; currentTime: number }
  | { type: "mediaElementInitialized" }
  | { type: "stepTransition"; step: Step }
  | { type: "sectionTransition"; section: Section; step: Step; playSettings: PlaySettings }
  | { type: "playClicked" }
  | { type: "systemPlay" }
  | { type: "pauseClicked" }
  | { type: "systemPause" }
  | { type: "skipClicked" }
  | { type: "replayClicked" }
  | { type: "dismissClicked" }
  | { type: "minimize" }
  | { type: "expand" }
  | { type: "terminate" }
  | AvatarMediaEvent;

export type PlaySettings = {
  autoPlay: boolean;
};

export interface AvatarContext {
  currentTime: number;

  isMediaElementAlreadyInitialized: boolean;
  playSettings?: PlaySettings;
  section?: Section;
  step?: Step;
}

export interface AvatarHooks {
  onAutoPlayMedia?: () => Promise<void>;
  onPlayMedia?: () => void;
  onPauseMedia?: () => void;
  onSkipStep?: (step: Step) => void;
  onReplayStep?: (step: Step) => void;
  onSyncMediaElementCurrentTime?: (time: number) => void;
  onFinishMedia?: () => void;
}

export interface CreateAvatarMachineParams {
  hooks?: AvatarHooks;
}

export const createAvatarActor = ({ hooks }: CreateAvatarMachineParams) => {
  const machine = setup({
    types: {
      events: {} as AvatarEvent,
      context: {} as AvatarContext,
    },
    guards: {
      isNotBlockingStep: ({ context }) => {
        const { step } = context;
        return !!step && !step.isBlockingStepType;
      },
      canReplay: ({ context }) => {
        const { step, currentTime } = context;
        if (!step) return false;
        const isEndOfStep = currentTime >= step.end;
        const isAtomStep = step.start == step.end;
        return step.isBlocking && isEndOfStep && !isAtomStep;
      },
      canPlay: ({ context }) => {
        const { currentTime, section, step } = context;
        if (!step || !section) return false;
        const isEndOfMedia = currentTime >= section.mediaDuration;
        const isEndOfStep = currentTime >= step.end;
        return !isEndOfMedia && (!step.isBlocking || !isEndOfStep);
      },
      shouldAutoPlay: ({ context }) => {
        const { playSettings } = context;
        return playSettings?.autoPlay ?? false;
      },
      isMediaElementAlreadyInitialized: ({ context }) => {
        return context.isMediaElementAlreadyInitialized;
      },
      isMediaFinished: ({ context }) => {
        const { currentTime, section } = context;
        if (!section) return false;
        return currentTime >= section.mediaDuration;
      },
    },
    actions: {
      setCurrentTime: assign(({ context, event }) => {
        if (event.type === "timeUpdate") {
          return { ...context, currentTime: event.currentTime };
        }
        return context;
      }),
      setCurrentSection: assign(({ context, event }) => {
        if (event.type === "sectionTransition") {
          return {
            ...context,
            section: event.section,
            step: event.step,
            playSettings: event.playSettings,
            isMediaElementAlreadyInitialized: event.section.id === context.section?.id,
          };
        }
        return context;
      }),
      setCurrentStep: assign(({ context, event }) => {
        if (event.type === "stepTransition") {
          return { ...context, step: event.step };
        }
        return context;
      }),
      skipStep: ({ context }) => {
        const { step } = context;
        hooks?.onSkipStep?.(step!);
      },
      playMedia: () => {
        debugLog("playing media");
        hooks?.onPlayMedia?.();
      },
      pauseMedia: () => {
        debugLog("pausing media");
        hooks?.onPauseMedia?.();
      },
      finishMedia: () => {
        hooks?.onFinishMedia?.();
      },
      replayStep: ({ context }) => {
        const { step } = context;
        debugLog("replaying step");
        hooks?.onReplayStep?.(step!);
      },
      syncMediaElementCurrentTime: ({ context }) => {
        hooks?.onSyncMediaElementCurrentTime?.(context.currentTime);
      },
    },
    actors: {
      tryAutoPlay: fromPromise(async () => {
        if (hooks?.onAutoPlayMedia) {
          return await hooks.onAutoPlayMedia();
        }
        return Promise.resolve();
      }),
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QEMBuyAuyBOBiDAlgLZgCqADhJmANoAMAuoqOQPawGGsB2zIAHogCMQgBwA2AHQBmAJwAWIbPHiATPPUBWADQgAnogC0qudMmrZ0upoDsN6Zs1qAvs91pMOXLDABjLtwAKtjI3BwB9ExIIGzhBDx8ggjSorLmViZWmlY2mqq6BgiGQuJ0NpLKoo4mskqqQtKu7uhYeLAYYOTBoXE8kXyxnPG80UlC8pqiknQmNrWqqhILOvpG6nRpslqidGJyotLyTSAerfhg2EQE3NT90YMBiWvi5UIbGrK5NmWLBYiKUjouyE32sjicNmOpy8V24xAIAC9aIwBuwhglRohFnJJCJRPIXjYJJotn8EHZVBUZnUUvJrPJGm4Ti0cJJrkNkAAbRHXKCSCB+AgCgDqyCG3CgADFWNgALKQAjIACinLAJG4GAAknDCFzEZhhrg7iw0Y9MQgDppJPIqjbdjZFiT8qsiuNNPJzLUshZxPIbEIoSzsGydYruQjefzBSKxYQJdK5Qrlar1VrQ3qEQaeEahFETb0RqAxqpJpJHBNNHtspYycUJh6LEJFqp-Q7UpCmdDg+zdeHIwB3WMJgDKfgC3TC6O43jHwwnBeNMVNwyeFtS1rU1hLdAc4lEQlrYhmkj3eX9eUOckDnm76b7Eskg84CflEEVKrVYA12o54az05IN9k0-b870RSBFweFdzSbepJCJDZxFkCRrGkBpDwWIRpgmKxRBbF4r07IMQ1-HkH1gPRuF8V93xTL8MAAYQAV2wbB6MCYgwCNFF7mXDEi0QSwpHqWRZB3ISlHkeRDzoP1rRbMSgWkOZSlUa9WkkbAmO4OEJW8ABrAhyAY7lfH0iCePzKdVzwk80I2aRMIWeoZOQ+CJD9Jt5C2FQO2aG9NO03SoFwQDFUldlYAACwsvMlwLVd6nELDbEsUTpCsWCyTeNI6CqFJviQ1Q93GdTWS0nTIwFXwhTAABBJiMFYAAFTlkD0bi4qg-iBEQUQJBkUobSk5C7BeWsHToE9ljsXYCTwo4iICirgqjGqBQaprWvanMur4wteoQYqPRJeZ7CbJwDxdQxJumktZvGPd1DK4MVsjZBGpatqOogHgwBDVBWHMyQPq277IP21cFCmfFHDQmwATEWs6Smor9xKBGZjmF7Asqh9Qa+naLmwGVJHINqMAAMxlIgQc+7a9AhhLzXEDLPT9FRmw0Jta2yMwDjKfcJhKBw1KWjS3ofcn2po5BOtRZmBIQN1KSqZK7FsCwqmdQpihSeClFyfEkIkOZFv8iWgsjNjpb0YcOnIbw9HaNUGaZ6yYOWeCbXhuxlKJaSXUcipDlmjK9wc0QcclvknwwF8kwZ3lQsT77eXds0lbxV5UiBEbZNsQPdcw6Y6CKl4kImA5o6tqW0708gPp8EyCDM2KFY9rOsYqcYtgBEs0KLoxximkSbCQ31ubUWQa7xvlbeTiiXaIZqm+RPbFcOjyT281ISwaJRWcPOlzENrZx5Kako-F8ra-ntfZfl3jN6SZC0jVulJkmLJxGynEL-6lseoeUGSz1WtVWqDMADy2AIpwmio-DO0Es5zFyjsHY48dxSRKGSX0MgCRzAQm8MQeEwFVWjGAaBsDIpRUQbmDumdDpiBKCeJsZcVAV1sDYY+J5SgbBBCSRQJQthkKlmvCAuBbYtzbhAJBPUxiOCwhMIkYkST8KRi6YqqsTALFkE4Q4IDxCiPvkxHwEibbfWkeZWRll4qdyYSCKa+48rJWsAcbyOssRqEkHhNC2QCRHhETfV6d8ybiKdsvN2tjuoHTGAteCFgB7eTmIcFYhQtE+JMEIfxyUdhBItrfOeYTTGQEfEOGUstV4lJTkBKpZi5GxMEk4HxIJHLJSQtYHcZInBTEQiWP0slvgpGMcUsxkhYTwiRBIsA-BG7cBsRvexSRip4LyOwokrMGSyFwQcA23lKyszQhYPyzJlqhMbiUiA4z2RXCmbgN8sAriwFgFY9uz8lnCAMbibIsxxKqTJBSKkCwDi5BcRoEZFyxlxwTIEC4sJ-znEuNcW40TIbmlmPBISvpz7czygCh0QKGgHHGCkaQRjgm41WpC0pYVkBwIINFSAM5-BzhCJOCIqKX5Ykch6OYeUZjiE0GXP0aS+pSAcP1TcCENYz2ONwVgAp4DRC7Aw5Bh1DAZStJaUSCgiRC2yLWeo9hpi1ErgyKSjICm3lIhGCUqr5GIARlIT4dg3gWCsGoaQhq8pWhJK0xwokRU4x7GGMifIIExnFFKCpSYPyph-L2fUjCYmrjNhUOw-oZiWDLiYQ8X9zCKGzbYDKuFg1gVtbHcp2BRwsp4POexKbzQSDMKpUETZBVKFFa6USvTahjXHhMVQO4y02oHFW2Wcb6IJtDZmZNaKs5iHKIoOQ4wpLNj0YeUW5hgTnVsMlBoI7E0VskBRKiE66IamYqxdinF7WNItHBOYOx+WWHqKIDCUkyw4QcHkdQJRNDGLvauEl0wVAZQmIK-0GRayiUpD+ko+rdipBGRG+q9NvpAabZ8UuqQhWbN9IKmDskfHVAcm8YVlrTmWyKQTBmmGlb4leMW7+6hWb+l5qWAW3x9yCtKJ8CF31Zb0aYXSMwl9x6swUB49910JhUiUGJf0Yg10OBGRY9q9tOjCeLA0aYerxhKAaESbK4x4LJVUahPCxVKNdkpWO58Mban1ygNpz5ei9N2lyazMoXrrolyBJXWwDRHAbAE+1XkrmECfAbGJFCeRhozENbprRKz3QOEOAGClMdRlgCE+8xhr9ioVDUOPQqvpchXV1nJsSCmyh4hUwBrLoTUNUPpQgpMkWQTj2mBlVINoUmfBk4UMQVoCQXg8kSJs18rV2bEZczrtRQOHIg0F6DQd8Q728o5PKDRZLkpm9l6lVyhSqki8hK0+GyVWAKpWXBcwyy+jJVofd5sqOFKpeInLEAEwABEGXIAAEaqggJtQmhQrIFe5blMDqTIO7c8QgP0lJV2BoJIcHVELPvQsc4qOpYAztkpkJNDYHkOZD0RwSlHUnNkY6a0Uo7dMwbtUlGKYHkXWZYWyYoBGQqfnITJEj3EI1qeqbpx9y51y4S3MgJFkwpYMtekmA6YzLoEZpDpAsfhjh+r1ExxL7H2BYVIpuHOrlCAXhWhBGIAeGbx7bNVwSsSCxQX8McmLA75zPu0razFCAsumzlGQrMMaLYAQC8pxMMog6HSwRxh0Y31A-f5bVUkYwohyjasDfpnC+KsKCvqMpHbaPFquCAA */
    id: "avatar",
    initial: "initializing",
    context: () => ({
      currentTime: 0,
      isMediaElementAlreadyInitialized: false,
    }),
    on: {
      timeUpdate: {
        actions: "setCurrentTime",
      },
      sectionTransition: {
        actions: "setCurrentSection",
        target: ".initializing.decideWaitingForMediaElementInitialization",
      },
      stepTransition: {
        actions: "setCurrentStep",
      },
      terminate: {
        target: ".terminated",
      },
      minimize: {
        target: ".running.paused.minimized",
      },
    },
    states: {
      initializing: {
        initial: "waitForSectionTransition",
        states: {
          decideWaitingForMediaElementInitialization: {
            always: [
              {
                guard: "isMediaElementAlreadyInitialized",
                target: "syncMediaElementCurrentTime",
              },
              {
                target: "waitForMediaElementInitialization",
              },
            ],
          },
          waitForSectionTransition: {
            on: {
              sectionTransition: {
                actions: "setCurrentSection",
                target: "decideWaitingForMediaElementInitialization",
              },
            },
          },
          waitForMediaElementInitialization: {
            on: {
              mediaElementInitialized: {
                target: "syncMediaElementCurrentTime",
              },
            },
          },
          syncMediaElementCurrentTime: {
            entry: "syncMediaElementCurrentTime",
            always: "#avatar.running.decideAutoPlay",
            description: "Seeks media element to current time",
          },
        },
      },
      running: {
        initial: "decideAutoPlay",
        on: {
          skipClicked: {
            guard: "isNotBlockingStep",
            actions: "skipStep",
          },
          mediaFinished: {
            target: ".paused.mediaFinished",
          },
        },
        states: {
          decideAutoPlay: {
            always: [
              {
                guard: "shouldAutoPlay",
                target: "autoPlay",
              },
              {
                target: "paused.pausedForDisabledAutoPlay",
              },
            ],
          },
          autoPlay: {
            invoke: {
              id: "autoPlay",
              src: "tryAutoPlay",
              onDone: {
                target: "playing",
              },
              onError: {
                target: "paused.autoPlayFailed",
              },
            },
          },
          playMedia: {
            entry: "playMedia",

            always: {
              target: "waitForMediaPlaying",
            },
          },
          replayStep: {
            entry: "replayStep",
            on: {
              systemPlay: {
                target: "playMedia",
              },
            },
          },
          waitForMediaPlaying: {
            on: {
              mediaPlaying: {
                target: "playing",
              },
            },
          },
          playing: {
            on: {
              pauseClicked: {
                target: "pauseMedia",
              },
              systemPause: {
                target: "pauseMedia",
              },
            },
          },
          pauseMedia: {
            entry: "pauseMedia",
            always: {
              target: "paused.waitForMediaPause",
            },
          },

          decidePlayOrFinishMedia: {
            always: [
              {
                guard: "isMediaFinished",
                target: "#avatar.running.paused.mediaFinished",
                actions: "finishMedia",
              },
              {
                target: "playMedia",
              },
            ],
          },
          paused: {
            initial: "idle",
            on: {
              playClicked: {
                guard: "canPlay",
                target: "playMedia",
              },
              replayClicked: {
                target: "replayStep",
                guard: "canReplay",
              },
              systemPlay: {
                target: "decidePlayOrFinishMedia",
              },
            },
            states: {
              idle: {},
              pausedForDisabledAutoPlay: {
                tags: ["pausedForDisabledAutoPlay"],
              },
              waitForMediaPause: {
                on: {
                  mediaPaused: {
                    target: "idle",
                  },
                },
              },
              autoPlayFailed: {
                tags: ["autoPlayFailed"],
              },
              minimized: {
                tags: ["minimized"],
                entry: "pauseMedia",
                on: {
                  expand: {
                    target: "#avatar.initializing.waitForSectionTransition",
                  },
                  dismissClicked: {
                    target: "waitForTermination",
                  },
                },
              },
              waitForTermination: {
                on: {
                  terminate: {
                    target: "#avatar.terminated",
                  },
                },
              },
              mediaFinished: {
                on: {
                  sectionTransition: {
                    actions: "setCurrentSection",
                    target: "#avatar.initializing.decideWaitingForMediaElementInitialization",
                  },
                },
              },
            },
          },
        },
      },
      terminated: {
        type: "final",
      },
    },
  });

  return createActor(machine, {
    inspect: (e) => {
      if (e.type === "@xstate.snapshot" && e.event.type !== "timeUpdate") {
        debugLog({ event: e.event, result: e.snapshot });
      }
    },
  });
};

export const createAvatarStream = (machine: ReturnType<typeof createAvatarActor>) => fromStream(machine);
